import { PageArea } from '../..'
import Loading from '../../../components/process-state-notifications/Loading'
import {
  SingleColumnSheetArea,
  SingleColumnSheetBody,
} from '../../../containers/SingleSheetV2'
import { BasicInformationTable } from '../../../containers/SingleSheetV2/table'
import { BasicInformatioTableDef } from '../../../containers/SingleSheetV2/table/model'
import { useSingleSheetFormValidation } from '../../../containers/SingleSheetV2/hooks/formValidation'
import { useSubmitButton } from '../../../containers/SingleSheetV2/hooks/submitButton'
import { useSingleSheetKeyBind } from '../../../containers/SingleSheetV2/hooks/keyBind'
import { MultiLineTextTabDef } from '../../../containers/SingleSheetV2/tab/model'
import { EditedFormData } from '../../../containers/SingleSheetV2/hooks/editedFormData'
import { ProjectFormModel } from '../model'
import { useApplicationFunctionUuid } from '../../../hooks/useApplicationFunctionUuid'
import {
  OnChangeProject,
  useProjectModelModifier,
} from '../hooks/projectModelModifier'
import { ProjectSubHeader } from './subheader'

// この単票は複数行テキストのタブを持たない。バリデーションフックの参照を安定させ
// セルのメモ化を壊さないよう、空配列を共有する。
const NO_MULTILINE_TEXT_TAB_DEFS: MultiLineTextTabDef[] = []

type Props = {
  functionExternalId: string
  // 基本情報テーブル定義。登録/情報それぞれのバリアントフックで生成して渡す。
  tableDef: BasicInformatioTableDef
  initialized: boolean
  isLoading: boolean
  edited: boolean
  editedFormData: EditedFormData
  formModel: ProjectFormModel
  onChangeProject: OnChangeProject
  submit: () => Promise<void>
  reload: () => Promise<void> | void
  // ダイアログ表示時の閉じる（× ボタン）。渡された時だけ表示する。
  onClose?: () => void
  // 以下はバリアント固有（登録画面のみ渡す）
  onDelete?: () => void
  deleteDisabled?: boolean
  onLogin?: () => void
  loginDisabled?: boolean
}

// ProjectEdit / ProjectInformation 共通のレイアウト＋共通配線。
// 取得/保存などのライフサイクル・テーブル定義は各ページから props で受け取る。
export const ProjectSheet = ({
  functionExternalId,
  tableDef,
  initialized,
  isLoading,
  edited,
  editedFormData,
  formModel,
  onChangeProject,
  submit,
  reload,
  onClose,
  onDelete,
  deleteDisabled,
  onLogin,
  loginDisabled,
}: Props) => {
  const functionUuid = useApplicationFunctionUuid(functionExternalId)
  const {
    validate,
    validateAllProperties,
    validationErrors,
    hasValidationErrors,
  } = useSingleSheetFormValidation(tableDef, NO_MULTILINE_TEXT_TAB_DEFS, edited)
  const formModelModifiers = useProjectModelModifier(onChangeProject, validate)
  const { onClickSubmitButton, submitButtonIsActive } = useSubmitButton(
    submit,
    edited,
    formModel,
    hasValidationErrors,
    validateAllProperties
  )
  useSingleSheetKeyBind(onClickSubmitButton, functionUuid)

  // この機能の権限が無いユーザーには画面を表示しない。
  if (!functionUuid) return <></>

  return (
    <PageArea>
      <Loading isLoading={isLoading} />
      {initialized && (
        <>
          <ProjectSubHeader
            submitIsActive={submitButtonIsActive}
            submit={onClickSubmitButton}
            reload={reload}
            edited={edited}
            onClose={onClose}
            onDelete={onDelete}
            deleteDisabled={deleteDisabled}
            onLogin={onLogin}
            loginDisabled={loginDisabled}
          />
          <SingleColumnSheetArea>
            <SingleColumnSheetBody>
              <BasicInformationTable
                dataUuid={formModel.uuid}
                tableDef={tableDef}
                formModel={formModel}
                formModelModifiers={formModelModifiers}
                editedFormData={editedFormData}
                validationErrors={validationErrors}
              />
            </SingleColumnSheetBody>
          </SingleColumnSheetArea>
        </>
      )}
    </PageArea>
  )
}
